import { create } from 'zustand';
import { chatHistoryApi } from '../lib/apiClient';
import { toast } from '../lib/toast';

export interface Conversation {
  id: string;
  userId: string;
  user?: { email: string; first_name: string; last_name: string };
  messages: any[];
  messageCount: number;
  toolInvocations: { toolName: string; success: boolean; iteration: number }[];
  status: string;
  errorMessage: string | null;
  widgetId: string | null;
  widgetName?: string;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  channel?: string;
  drivingMode?: boolean;
  language?: string;
  llmProvider?: string;
  llmModel?: string;
  sttProvider?: string;
  ttsProvider?: string;
  ttsModel?: string;
  ttsVoice?: string;
}

export interface ChatHistoryFilters {
  status: string;
  widgetId: string;
  search: string;
  dateFrom: string;
  dateTo: string;
}

const PAGE_SIZE = 20;

interface ChatHistoryState {
  conversations: Conversation[];
  page: number;
  pageCount: number;
  loading: boolean;
  filters: ChatHistoryFilters;
  selectedIds: string[];
  fetchConversations: () => Promise<void>;
  getConversation: (id: string) => Promise<Conversation>;
  deleteConversation: (id: string) => Promise<void>;
  bulkDeleteConversations: (ids: string[]) => Promise<void>;
  clearAllConversations: (widgetId?: string) => Promise<void>;
  setPage: (page: number) => void;
  setFilters: (partial: Partial<ChatHistoryFilters>) => void;
  setSelectedIds: (ids: string[]) => void;
}

export const useChatHistoryStore = create<ChatHistoryState>((set, get) => ({
  conversations: [],
  page: 1,
  pageCount: 1,
  loading: false,
  filters: { status: '', widgetId: '', search: '', dateFrom: '', dateTo: '' },
  selectedIds: [],

  fetchConversations: async () => {
    const { page, filters } = get();
    set({ loading: true });
    try {
      const data = await chatHistoryApi.list(page, PAGE_SIZE, {
        status: filters.status || undefined,
        search: filters.search || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        widgetId: filters.widgetId || undefined,
      });
      set({
        conversations: data.conversations || [],
        pageCount: Math.ceil((data.total || 0) / PAGE_SIZE),
        selectedIds: [],
      });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load conversations');
    } finally {
      set({ loading: false });
    }
  },

  getConversation: async (id: string) => {
    return chatHistoryApi.get(id);
  },

  deleteConversation: async (id: string) => {
    await chatHistoryApi.delete(id);
    toast.success('Conversation deleted');
    await get().fetchConversations();
  },

  bulkDeleteConversations: async (ids: string[]) => {
    await chatHistoryApi.bulkDelete(ids);
    toast.success(`${ids.length} conversation(s) deleted`);
    await get().fetchConversations();
  },

  clearAllConversations: async (widgetId?: string) => {
    await chatHistoryApi.clearAll(widgetId);
    toast.success(widgetId ? 'Conversations cleared for widget' : 'All conversations cleared');
    await get().fetchConversations();
  },

  setPage: (page: number) => {
    set({ page });
  },

  setFilters: (partial: Partial<ChatHistoryFilters>) => {
    set(state => ({ filters: { ...state.filters, ...partial }, page: 1 }));
  },

  setSelectedIds: (ids: string[]) => {
    set({ selectedIds: ids });
  },
}));
